import { Button, Modal, toast } from "@devhop/ui";
import { useParams } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  VehicleConfigurationCreateInput,
  VehicleConfigurationUpdateInput,
} from "@/modules/catalog/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { VehicleConfigurationLookupItem } from "../api/client";
import {
  useCatalogVehicleConfigurationsQuery,
  useCatalogVehicleMakeQuery,
  useCatalogVehicleModelsQuery,
  useCreateVehicleConfiguration,
  useDeleteVehicleConfiguration,
  useUpdateVehicleConfiguration,
} from "../api/queries";
import { VehicleConfigurationForm } from "../ui/VehicleConfigurationForm";
import { VehicleConfigurationsTable } from "../ui/VehicleConfigurationsTable";
import { VehicleModelCodesSection } from "../ui/VehicleModelCodesSection";

const DEFAULT_CONFIG_LIMIT = 20;

export function CatalogVehicleModelDetailPage() {
  const { makeId, modelId } = useParams({
    from: "/app/catalog/vehicles/$makeId/models/$modelId",
  });
  const [configPage, setConfigPage] = useState({
    limit: DEFAULT_CONFIG_LIMIT,
    offset: 0,
  });

  const canCreate = useActionPermission(["catalog:create"]);
  const canManage = useActionPermission(
    ["catalog:update", "catalog:delete"],
    "any",
  );

  const configLimit = configPage.limit;
  const configOffset = configPage.offset;

  const makeQuery = useCatalogVehicleMakeQuery(makeId ?? "");
  const modelsQuery = useCatalogVehicleModelsQuery({
    makeId: makeId ?? undefined,
    limit: 10,
    offset: 0,
  });
  const configurationsQuery = useCatalogVehicleConfigurationsQuery({
    makeId: makeId ?? undefined,
    modelId: modelId ?? undefined,
    limit: configLimit,
    offset: configOffset,
  });

  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configSubmitting, setConfigSubmitting] = useState(false);
  const [editingConfig, setEditingConfig] =
    useState<VehicleConfigurationLookupItem | null>(null);

  const createConfig = useCreateVehicleConfiguration();
  const updateConfig = useUpdateVehicleConfiguration();
  const deleteConfig = useDeleteVehicleConfiguration();

  const make = makeQuery.data ?? null;
  const modelRows = modelsQuery.data?.data ?? [];
  const configListResult = configurationsQuery.data;
  const configRows = configListResult?.data ?? [];
  const configMeta = configListResult?.meta ?? {
    total: 0,
    limit: configLimit,
    offset: configOffset,
  };
  const model = useMemo(
    () => modelRows.find((m) => m.id === modelId) ?? null,
    [modelRows, modelId],
  );

  const makeOptions = useMemo(
    () => (make ? [{ value: make.id, label: make.name }] : []),
    [make],
  );
  const modelOptions = useMemo(
    () =>
      modelRows.map((m) => ({
        value: m.id,
        label: m.name,
        makeId: m.makeId,
      })),
    [modelRows],
  );

  if (!makeId || !modelId) {
    return (
      <Main>
        <p className="text-muted-foreground">ບໍ່ພົບລາຍລະອຽດຮຸ່ນ</p>
      </Main>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Catalog", to: "/app/catalog" },
            { label: "ຍີ່ຫໍ້ລົດ", to: "/app/catalog/vehicles" },
            {
              label: make?.name ?? "…",
              to: `/app/catalog/vehicles/${makeId}`,
            },
            { label: model?.name ?? "ກຳລັງໂຫຼດ..." },
          ]}
        />
      </Header>

      <Main className="space-y-6">
        <div className="mb-2 flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              {make?.name ?? "…"} — {model?.name ?? "ກຳລັງໂຫຼດ..."}
            </h2>
            <p className="text-muted-foreground text-sm">
              ຈັດການ vehicle configurations (ປີ, engine, model code) — ເພີ່ມ
              spec/option codes ໃນຄລັງຮຸ່ນດ້ານລຸ່ມ ແລ້ວເລືອກຜູກກັບແຕ່ລະ
              configuration ໃນໜ້າ EPC
            </p>
          </div>
          {canCreate && makeId && (
            <Button
              onClick={() => {
                setEditingConfig(null);
                setConfigModalOpen(true);
              }}
            >
              <PlusIcon className="size-4" />
              ເພີ່ມ configuration
            </Button>
          )}
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <div className="mb-2 px-2">
            <h3 className="font-semibold text-base">Configurations</h3>
          </div>

          <VehicleConfigurationsTable
            canManage={!!canManage}
            isLoading={configurationsQuery.isLoading}
            data={configRows}
            offset={configMeta.offset}
            limit={configMeta.limit}
            totalCount={configMeta.total}
            onPaginationChange={(offset, limit) =>
              setConfigPage({ offset, limit })
            }
            onEdit={(row) => {
              setEditingConfig(row);
              setConfigModalOpen(true);
            }}
            onDelete={async (id) => {
              try {
                await deleteConfig.mutateAsync(id);
                toast.success("ລຶບ configuration ສຳເລັດ");
                await configurationsQuery.refetch();
              } catch (err: unknown) {
                const msg =
                  err && typeof err === "object" && "message" in err
                    ? String((err as { message: string }).message)
                    : "ມີ fitment ຜູກຢູ່ ບໍ່ສາມາດລຶບໄດ້";
                toast.error(msg);
              }
            }}
          />
        </div>

        <VehicleModelCodesSection modelId={modelId} />
      </Main>

      <Modal
        open={configModalOpen}
        onOpenChange={(open) => {
          setConfigModalOpen(open);
          if (!open) setEditingConfig(null);
        }}
        title={editingConfig ? "ແກ້ໄຂ configuration" : "ເພີ່ມ configuration"}
        size="md"
      >
        <VehicleConfigurationForm
          key={editingConfig?.id ?? "new"}
          initialValues={
            editingConfig
              ? {
                  makeId: editingConfig.makeId,
                  modelId: editingConfig.modelId,
                  modelCode: editingConfig.modelCode ?? "",
                  yearFrom: editingConfig.yearFrom?.toString() ?? "",
                  monthFrom: editingConfig.monthFrom?.toString() ?? "",
                  yearTo: editingConfig.yearTo?.toString() ?? "",
                  monthTo: editingConfig.monthTo?.toString() ?? "",
                  engine: editingConfig.engine ?? "",
                  marketCode: editingConfig.marketCode ?? "",
                  transmissionCode: editingConfig.transmissionCode ?? "",
                  engineCode: editingConfig.engineCode ?? "",
                  gradeCode: editingConfig.gradeCode ?? "",
                  adminNotes: editingConfig.adminNotes ?? "",
                }
              : makeId && modelId
                ? {
                    makeId,
                    modelId,
                    modelCode: "",
                    yearFrom: "",
                    monthFrom: "",
                    yearTo: "",
                    monthTo: "",
                    engine: "",
                    marketCode: "",
                    transmissionCode: "",
                    engineCode: "",
                    gradeCode: "",
                    adminNotes: "",
                  }
                : undefined
          }
          makeOptions={makeOptions}
          modelOptions={modelOptions}
          onSubmit={async (vals) => {
            try {
              setConfigSubmitting(true);
              if (editingConfig) {
                await updateConfig.mutateAsync({
                  id: editingConfig.id,
                  input: vals as VehicleConfigurationUpdateInput,
                });
                toast.success("ແກ້ໄຂ configuration ສຳເລັດ");
              } else {
                await createConfig.mutateAsync(
                  vals as VehicleConfigurationCreateInput,
                );
                toast.success("ເພີ່ມ configuration ສຳເລັດ");
              }
              setConfigModalOpen(false);
              setEditingConfig(null);
              await configurationsQuery.refetch();
            } catch (err: unknown) {
              toast.error(
                (err as { message?: string })?.message ?? "ບັນທຶກບໍ່ສຳເລັດ",
              );
            } finally {
              setConfigSubmitting(false);
            }
          }}
          onCancel={() => setConfigModalOpen(false)}
          submitting={configSubmitting}
        />
      </Modal>
    </>
  );
}
